import { useState } from 'react';
import { Link } from 'react-router';
import type { LoginModalType, PostAuthOptions } from 'types';
import type { Image } from 'types/capi/shared';

import ChevronIcon from '~/assets/icons/Chevron';
import VectorIcon from '~/assets/icons/Vector';
import WarningIcon from '~/assets/icons/Warning';
import useUser from '~/hooks/useUser';
import { isAppleMobileDevice } from '~/utils/browser';

import { Dialog, DialogDismiss } from '../../../Dialog';
import DownloadAppFooter from '../DownloadAppFooter';
import InitialScreen from '../InitialScreen';
import EmailLogin from '../LoginFlow';
import SignUpCompleteScreen from './SignUpCompleteScreen';
import SignUpScreen from './SignUpScreen';

export enum Stage {
  Error = -1,
  Initial = 0,
  SignUp = 1,
  Complete = 2,
  Login = 3
}

type Props = {
  directSignUp?: boolean;
  hideAppDownloadPrompt?: boolean;
  image?: Image;
  modalType: LoginModalType;
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  postAuthOptions?: PostAuthOptions;
};

export default function SignUp({
  onDismiss,
  postAuthOptions,
  image,
  modalType,
  directSignUp = false,
  hideAppDownloadPrompt = false
}: Props) {
  const [stage, setStage] = useState(
    directSignUp ? Stage.SignUp : Stage.Initial
  );
  const [error, setError] = useState<string | null>(null);
  const user = useUser();

  function onBackNavigation() {
    setError(null);
    setStage(stage - 1);
  }

  if (user.isLoggedIn) return null;

  const isWhiteBackground = stage === Stage.Initial;
  const modalBackground = isWhiteBackground
    ? 'bg-white'
    : 'border border-white bg-black';

  const showAppDownload =
    isAppleMobileDevice(navigator.userAgent) &&
    !hideAppDownloadPrompt &&
    stage === Stage.Initial &&
    !['purchase', 'profile'].includes(modalType);

  if (stage === Stage.Login) {
    return (
      <EmailLogin
        onBackNavigation={() => setStage(Stage.Initial)}
        modalType={modalType}
        onDismiss={onDismiss}
        postAuthOptions={postAuthOptions}
      />
    );
  }

  return (
    <Dialog
      isOpen={true}
      onDismiss={onDismiss}
      className={`z-50 rounded-lg p-4 max-md:mx-4  ${modalBackground}`}
    >
      <div
        className="my-4 flex w-auto max-w-[500px] flex-col"
        data-cy="login-modal"
      >
        {stage !== Stage.Initial && (
          <nav className="flex flex-row items-center justify-between">
            <button className="rotate-90" onClick={onBackNavigation}>
              <ChevronIcon stroke="white" width="14" height="14" />
            </button>
            {modalType === 'profile' ? (
              <Link to="/">
                <VectorIcon fill="white" width="10" height="10" />
              </Link>
            ) : (
              <DialogDismiss className="text-black">
                <VectorIcon fill="white" width="10" height="10" />
              </DialogDismiss>
            )}
          </nav>
        )}
        <Screen
          stage={stage}
          onDismiss={onDismiss}
          setStage={setStage}
          postAuthOptions={postAuthOptions}
          onError={setError}
          image={image}
          modalType={modalType}
        />
        {error && (
          <ErrorMessage error={error} isWhiteBackground={isWhiteBackground} />
        )}
        {showAppDownload && <DownloadAppFooter modalType={modalType} />}
      </div>
    </Dialog>
  );
}

export type ScreenProps = {
  image?: Image;
  modalType: LoginModalType;
  onDismiss: (options?: { loginSuccessful?: boolean }) => void;
  onError: (err: string | null) => void;
  postAuthOptions?: PostAuthOptions;
  setStage: (name: Stage) => void;
  stage: Stage;
};

function Screen(props: ScreenProps) {
  const [email, setEmail] = useState('');

  function onLogInClick() {
    props.setStage(Stage.Login);
  }
  function onSignUpClick() {
    props.setStage(Stage.SignUp);
  }

  switch (props.stage) {
    case Stage.Initial:
      return (
        <InitialScreen
          {...props}
          onSignUpClick={onSignUpClick}
          onLogInClick={onLogInClick}
        />
      );
    case Stage.SignUp:
      return <SignUpScreen {...props} setEmail={setEmail} />;
    case Stage.Complete:
      return <SignUpCompleteScreen {...props} email={email} />;
  }
}

function ErrorMessage({
  error,
  isWhiteBackground
}: {
  error: string;
  isWhiteBackground: boolean;
}) {
  return (
    <p
      className={`mt-4 flex items-center gap-2 rounded p-2 text-sm font-normal ${
        isWhiteBackground
          ? 'bg-red-alert/10 text-red-alert'
          : 'bg-red-alert/75 text-white'
      }`}
    >
      <span className="w-6">
        <WarningIcon fill={isWhiteBackground ? '#D71225' : 'white'} />
      </span>
      {error}
    </p>
  );
}
